import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { IntelActorReport, IntelReportCallsInput, IntelReportCursor, IntelReportScorecard } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { CallRow } from './CallRow';
import AdminLayout from '@/components/Layout/AdminLayout';
import { Button } from '@/components/UI';
import { useAppQuery } from '@/hooks/useAppQuery';
import {
    basisChoices,
    captureChoices,
    cohortChoices,
    directionChoices,
    eastern,
    edge,
    horizons,
    kindChoices,
    percent,
    skillLabel,
    words
} from './reportModel';

const panel = 'rounded-xl border border-slate-700 bg-slate-900 p-5';
const selectClass = 'rounded border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100';
const cell = 'border-t border-slate-800 px-3 py-3 text-left align-top';

function Choice<T extends string | number>({
    label,
    value,
    choices,
    onChange
}: {
    label: string;
    value: T;
    choices: readonly { value: T; label: string }[];
    onChange: (value: T) => void;
}) {
    return (
        <label className="flex flex-col gap-1 text-sm text-slate-400">
            {label}
            <select
                className={selectClass}
                value={value}
                onChange={(event) => {
                    const choice = choices.find((item) => String(item.value) === event.target.value);
                    if (choice) onChange(choice.value);
                }}
            >
                {choices.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                        {choice.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

export default function InfluencerCalls() {
    const [params, setParams] = useSearchParams();
    const handle = params.get('handle')?.replace(/^@/, '') ?? '';
    const [search, setSearch] = useState(handle);
    const actors = useAppQuery(trpc.intelReports.listActors.queryOptions(), { fallbackErrorMessage: 'Could not load tracked accounts' });
    useEffect(() => {
        if (!handle && actors.data?.items[0]) setParams({ handle: actors.data.items[0].handle }, { replace: true });
    }, [handle, actors.data, setParams]);
    useEffect(() => setSearch(handle), [handle]);
    return (
        <AdminLayout containerSize="1600px" title="Influencer calls">
            <div className="space-y-5 text-slate-100">
                <div className="flex flex-wrap items-end gap-3">
                    <Choice
                        label="Tracked X account"
                        value={handle}
                        choices={(actors.data?.items ?? []).map((actor) => ({
                            value: actor.handle,
                            label: `@${actor.handle} · ${actor.displayName}`
                        }))}
                        onChange={(value) => setParams({ handle: value })}
                    />
                    <form
                        className="flex items-end gap-2"
                        onSubmit={(event) => {
                            event.preventDefault();
                            setParams({ handle: search.trim().replace(/^@/, '') });
                        }}
                    >
                        <label className="flex flex-col gap-1 text-sm text-slate-400">
                            Find a stored handle
                            <input
                                className={selectClass}
                                maxLength={16}
                                pattern="@?[A-Za-z0-9_]{1,15}"
                                required
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                            />
                        </label>
                        <Button type="submit">Open report</Button>
                    </form>
                </div>
                {actors.error && (
                    <p role="alert" className="text-red-300">
                        {actors.error.message}
                    </p>
                )}
                {actors.data?.hasMore && (
                    <p className="text-sm text-amber-200">
                        The selector shows the first 200 accounts. Open another stored account by its handle.
                    </p>
                )}
                {handle ? (
                    <Report key={handle.toLowerCase()} handle={handle} />
                ) : (
                    <p className="text-slate-400">{actors.isLoading ? 'Loading accounts…' : 'No tracked accounts found.'}</p>
                )}
            </div>
        </AdminLayout>
    );
}

function Report({ handle }: { handle: string }) {
    const [horizonDays, setHorizonDays] = useState<number>(7);
    const [cohort, setCohort] = useState<IntelReportScorecard['cohort']>('hindsight_free');
    const [basis, setBasis] = useState<IntelReportScorecard['basis']>('market');
    const [segment, setSegment] = useState<IntelReportScorecard['segment']>('all');
    const report = useAppQuery(trpc.intelReports.getReport.queryOptions({ handle }), {
        fallbackErrorMessage: 'Could not load the influencer report'
    });
    if (report.isLoading) return <p>Loading report…</p>;
    if (report.error)
        return (
            <p role="alert" className="text-red-300">
                {report.error.message}
            </p>
        );
    if (!report.data) return <p>No report available.</p>;
    const data = report.data;
    const cards = data.scorecards.filter((card) => card.cohort === cohort && card.basis === basis && card.segment === segment);
    const selected = cards.find((card) => card.horizonDays === horizonDays);
    return (
        <>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-2xl font-semibold">
                    {data.actor.displayName} <span className="text-slate-400">@{data.actor.handle}</span>
                </h2>
                <p className="text-sm text-slate-400">
                    Tracking {words(data.actor.trackingState)} · account {words(data.actor.accountStatus)}
                </p>
            </div>
            <div className="grid gap-5 xl:grid-cols-[2fr_1fr]">
                <section className={panel} aria-label="Call performance">
                    <h3 className="mb-4 text-lg font-semibold">Call performance</h3>
                    <div className="mb-5 grid gap-3 sm:grid-cols-2">
                        <Choice label="Evidence sample" value={cohort} choices={cohortChoices} onChange={setCohort} />
                        <Choice label="Comparison" value={basis} choices={basisChoices} onChange={setBasis} />
                        <Choice label="Direction" value={segment} choices={directionChoices} onChange={setSegment} />
                        <Choice
                            label="Holding period"
                            value={horizonDays}
                            choices={horizons.map((value) => ({ value, label: `${value} calendar days` }))}
                            onChange={setHorizonDays}
                        />
                    </div>
                    <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <Metric label="Completed episodes" value={selected ? String(selected.maturedCount) : '—'} />
                        <Metric label="Beat comparison" value={percent(selected?.hitRate ?? null)} />
                        <Metric label="Average edge" value={edge(selected?.meanExcessPercent ?? null)} />
                        <Metric label="Evidence class" value={selected ? skillLabel[selected.skillClass] : 'Not enough samples'} />
                    </div>
                    <p className="mb-3 text-sm leading-relaxed text-slate-400">
                        {basis === 'market'
                            ? 'Success means a positive return relative to SPY in the called direction.'
                            : 'Success means beating historical random entries in the same stock, after accounting for SPY and the called direction.'}{' '}
                        These are underlying price returns from the first session opening after publication. Repeated posts share one
                        episode; looking-back claims and conditional calls add no directional sample. Conditional price targets are judged
                        separately below.
                    </p>
                    {cards.length === 0 && (
                        <p className="mb-3 text-sm text-amber-200">No scorecard has been computed for this sample yet.</p>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    {['Days', 'Completed', 'Independent', 'Pending', 'Excluded', 'Success', 'Avg. edge'].map((label) => (
                                        <th key={label} className="px-3 py-2 text-left text-slate-400">
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {horizons.map((days) => {
                                    const card = cards.find((item) => item.horizonDays === days);
                                    return (
                                        <tr key={days} className={days === horizonDays ? 'bg-slate-800' : ''}>
                                            <td className={cell}>
                                                <button className="text-sky-300 underline" onClick={() => setHorizonDays(days)}>
                                                    {days}
                                                </button>
                                            </td>
                                            <td className={cell}>{card?.maturedCount ?? '—'}</td>
                                            <td className={cell}>{card?.independentSampleCount ?? '—'}</td>
                                            <td className={cell}>{card?.immatureCount ?? '—'}</td>
                                            <td className={cell}>{card?.excludedCount ?? '—'}</td>
                                            <td className={cell}>{percent(card?.hitRate ?? null)}</td>
                                            <td className={cell}>{edge(card?.meanExcessPercent ?? null)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-3 text-xs text-slate-400">
                        Scores refresh nightly. Overlapping holdings in the same stock count as independent blocks for the evidence class.
                        Last computed: {eastern(selected?.computedAt ?? null)} ET.
                    </p>
                </section>
                <Coverage data={data} />
            </div>
            <Targets records={data.targetRecords} />
            <CallHistory actorId={data.actor.actorId} horizonDays={horizonDays} />
        </>
    );
}

function Metric({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-lg bg-slate-800 p-3">
            <div className="text-xs text-slate-400">{label}</div>
            <div className="mt-1 text-lg font-semibold">{value}</div>
        </div>
    );
}

function Coverage({ data }: { data: IntelActorReport }) {
    const coverage = data.coverage;
    return (
        <section className={panel} aria-label="Collection coverage">
            <h3 className="mb-4 text-lg font-semibold">Collection coverage</h3>
            <dl className="space-y-3 text-sm">
                {[
                    ['Live collection began', eastern(data.actor.liveCaptureStartedAt)],
                    ['Retained posts', `${coverage.retainedPosts} (${coverage.livePosts} live, ${coverage.backfillPosts} backfilled)`],
                    ['Oldest retained post', eastern(coverage.oldestPostAt)],
                    ['Newest retained post', eastern(coverage.newestPostAt)],
                    ['Last capture check', eastern(coverage.lastCaptureAt)],
                    ['Awaiting extraction', coverage.pendingPosts],
                    ['Held for validation / failed', `${coverage.quarantinedPosts} / ${coverage.failedPosts}`],
                    ['Recorded gaps / still open', `${coverage.recordedGaps} / ${coverage.openGaps}`],
                    ['Consecutive capture failures', coverage.consecutiveFailures]
                ].map(([label, value]) => (
                    <div key={label} className="flex flex-wrap justify-between gap-2 border-b border-slate-800 pb-2">
                        <dt className="text-slate-400">{label}</dt>
                        <dd>{value}</dd>
                    </div>
                ))}
            </dl>
            <p className="mt-4 text-sm leading-relaxed text-amber-100">
                Coverage is partial. An old quoted post does not establish a complete archive. Replies and reposts are outside call
                extraction; deleted content is removed. Extraction can miss or misread calls.
            </p>
            <p className="mt-3 text-sm text-slate-400">
                Options show underlying-stock outcomes. Cryptocurrency and cash-index outcomes are currently unpriced. Reported trades and
                holdings are the author's claims.
            </p>
        </section>
    );
}

function Targets({ records }: { records: IntelActorReport['targetRecords'] }) {
    return (
        <section className={panel} aria-label="Stated price targets">
            <h3 className="text-lg font-semibold">Stated price targets</h3>
            <p className="my-3 text-sm text-slate-400">
                Original targets and later revisions are kept separate. Pending targets remain unresolved; open-ended hits have no promised
                deadline. These records include all stored history and both directions, independently of the performance filters above.
            </p>
            {records.length === 0 ? (
                <p className="text-sm text-slate-400">No evaluated target record yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr>
                                {['Claim', 'Deadline', 'Hit', 'Pending', 'Stopped', 'Expired', 'Ambiguous'].map((label) => (
                                    <th key={label} className="px-3 py-2 text-left text-slate-400">
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {records.flatMap((record) => [
                                <tr key={`${record.claimRank}-dated`}>
                                    <td className={cell}>{words(record.claimRank)}</td>
                                    <td className={cell}>Stated</td>
                                    <td className={cell}>{record.timeBoundedHitCount}</td>
                                    <td className={cell}>{record.timeBoundedPendingCount}</td>
                                    <td className={cell}>{record.timeBoundedStoppedCount}</td>
                                    <td className={cell}>{record.timeBoundedExpiredCount}</td>
                                    <td className={cell}>{record.timeBoundedAmbiguousCount}</td>
                                </tr>,
                                <tr key={`${record.claimRank}-open`}>
                                    <td className={cell}>{words(record.claimRank)}</td>
                                    <td className={cell}>Open-ended</td>
                                    <td className={cell}>{record.openEndedHitCount}</td>
                                    <td className={cell}>{record.openEndedPendingCount}</td>
                                    <td className={cell}>{record.openEndedStoppedCount}</td>
                                    <td className={cell}>—</td>
                                    <td className={cell}>{record.openEndedAmbiguousCount}</td>
                                </tr>
                            ])}
                        </tbody>
                    </table>
                    <div className="mt-3 space-y-1 text-sm text-slate-400">
                        {records.map((record) => (
                            <p key={record.claimRank}>
                                {words(record.claimRank)}: {record.unscoredCount} unscored. Among {record.baselineTargetCount} targets
                                compared over the same elapsed window, {record.baselineHitCount} hit versus{' '}
                                {record.baselineExpectedHitCount.toFixed(1)} expected from historical entries. Computed{' '}
                                {eastern(record.computedAt)} ET.
                            </p>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

function CallHistory({ actorId, horizonDays }: { actorId: number; horizonDays: number }) {
    const [captureMode, setCaptureMode] = useState<IntelReportCallsInput['captureMode']>('all');
    const [direction, setDirection] = useState<IntelReportCallsInput['direction']>('all');
    const [statementKind, setStatementKind] = useState<IntelReportCallsInput['statementKind']>('all');
    const [cursors, setCursors] = useState<(IntelReportCursor | undefined)[]>([undefined]);
    const calls = useAppQuery(
        trpc.intelReports.listCalls.queryOptions({
            actorId,
            horizonDays,
            captureMode,
            direction,
            statementKind,
            cursor: cursors.at(-1)
        }),
        { fallbackErrorMessage: 'Could not load call history' }
    );
    const changeFilter = <T,>(setter: (value: T) => void, value: T) => {
        setter(value);
        setCursors([undefined]);
    };
    return (
        <section className={panel} aria-label="Call history">
            <h3 className="text-lg font-semibold">Call history</h3>
            <p className="my-3 text-sm text-slate-400">
                Each row is one extracted claim. The {horizonDays}-day result against SPY appears only on the first eligible claim in an
                episode. These history filters apply to posts; performance above uses grouped episodes.
            </p>
            <div className="mb-4 flex flex-wrap gap-3">
                <Choice
                    label="Post collection"
                    value={captureMode}
                    choices={captureChoices}
                    onChange={(value) => changeFilter(setCaptureMode, value)}
                />
                <Choice
                    label="Claim direction"
                    value={direction}
                    choices={directionChoices}
                    onChange={(value) => changeFilter(setDirection, value)}
                />
                <Choice
                    label="Claim type"
                    value={statementKind}
                    choices={kindChoices}
                    onChange={(value) => changeFilter(setStatementKind, value)}
                />
            </div>
            {calls.error && (
                <p role="alert" className="text-red-300">
                    {calls.error.message}
                </p>
            )}
            {calls.isLoading ? (
                <p>Loading calls…</p>
            ) : (
                calls.data && (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr>
                                        {['Published (ET)', 'Instrument', 'Claim and source', `${horizonDays}-day edge vs SPY`].map(
                                            (label) => (
                                                <th key={label} className="px-3 py-2 text-left text-slate-400">
                                                    {label}
                                                </th>
                                            )
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {calls.data.items.map((call) => (
                                        <CallRow key={call.eventId} call={call} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {calls.data.items.length === 0 && <p className="py-5 text-slate-400">No calls match these filters.</p>}
                        <div className="mt-4 flex items-center gap-3">
                            <Button
                                disabled={cursors.length === 1 || calls.isFetching}
                                onClick={() => setCursors((items) => items.slice(0, -1))}
                            >
                                Newer
                            </Button>
                            <span className="text-sm text-slate-400">Page {cursors.length}</span>
                            <Button
                                disabled={!calls.data.nextCursor || calls.isFetching}
                                onClick={() => {
                                    if (calls.data?.nextCursor) setCursors((items) => [...items, calls.data.nextCursor ?? undefined]);
                                }}
                            >
                                Older
                            </Button>
                        </div>
                    </>
                )
            )}
        </section>
    );
}
